import { Component, OnInit, Inject, ErrorHandler, ViewChild, HostListener, OnDestroy, ChangeDetectionStrategy, Injector, afterNextRender, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { disabled, FieldTree, form, required } from '@angular/forms/signals'

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import {
  Kirjanpitotili,
  AlvMaaritys,
  OstoAlvt,
  MyyntiAlvt,
  LokalisoituKirjanpitotilinAlvTyyppi,
  LokalisoidutAlvtyypit,
  KirjanpitotilinAlvTyyppi,
  MonikielinenTeksti,
} from 'app/_jaettu-lemonator/model/kirjanpito'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'

import { debounceTime, startWith, takeUntil } from 'rxjs/operators'
import { Subject, firstValueFrom } from 'rxjs'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { FlatKirjanpitotili } from './asiakkaan-tilikartta-edit.component'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'

export interface AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialogData {
  tili: Kirjanpitotili
  tilit: FlatKirjanpitotili[]
  asiakas: Asiakas
  lisaa: boolean
}

interface PaatilikartanTiliLomake {
  numero: string | null
  nimiFi: string
  nimiEn: string
  alv: AlvMaaritys | null
  alvtyyppi: KirjanpitotilinAlvTyyppi | null
  aktiivinen: boolean | null
  reskontraActive: boolean | null
  automaattinenPoisto: boolean
  automaattinenPoistoProsentti: number | null
  automaattinenPoistoTili: Kirjanpitotili | null
}

@Component({
  templateUrl: './kirjanpitotili-muokkaa-paatilikartan-tilia.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialog implements OnInit, OnDestroy {
  @ViewChild('nimiFiInput', { read: MatInput, static: true }) nimiFiInput: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _alvPoissa = signal(false)
  private readonly _poistotPoissa = signal(false)

  commonError: string = ''
  lomake: FieldTree<PaatilikartanTiliLomake>
  lisaa = false
  alvt: AlvMaaritys[] = [].concat(OstoAlvt.kaikki).concat(MyyntiAlvt.kaikki)
  lokalisoidutAlvtyypit: LokalisoituKirjanpitotilinAlvTyyppi[] = LokalisoidutAlvtyypit.kaikki
  suodatetutTilitSubject: Subject<FlatKirjanpitotili[]> = new Subject<[]>()

  vertaaAlvMaarityksia: (a: AlvMaaritys, b: AlvMaaritys) => boolean = (a: AlvMaaritys, b: AlvMaaritys): boolean => {
    return a && b && a.tunniste === b.tunniste
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialogData,
    private _dialogRef: MatDialogRef<AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialog, boolean>,
    private _errorHandler: ErrorHandler,
    private _tilikarttaService: TilikarttaService,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _dialog: MatDialog,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {
    this.lisaa = this._data.lisaa
  }

  ngOnInit() {
    this.lomake = form(
      signal<PaatilikartanTiliLomake>({
        numero: this._data.tili.numero ?? null,
        nimiFi: this._data.tili.localisedName?.fi ?? this._data.tili.nimi ?? '',
        nimiEn: this._data.tili.localisedName?.en ?? '',
        alv: this._data.tili.oletusAlvKasittely ?? null,
        alvtyyppi: null,
        aktiivinen: this._data.tili.aktiivinen ?? null,
        reskontraActive: this._data.tili.reskontraActive ?? null,
        automaattinenPoisto: !!this._data.tili.poistotiedot,
        automaattinenPoistoProsentti: this._data.tili?.poistotiedot?.poistoprosentti || 25,
        automaattinenPoistoTili: this._data.tilit.find(tili => tili.kirjanpitotili.numero === this._data.tili?.poistotiedot?.poistotili)?.kirjanpitotili || null,
      }),
      polku => {
        disabled(polku.numero)
        required(polku.numero)
        validoiReaktiivisesti(polku.numero, Validators.maxLength(5))
        validoiReaktiivisesti(polku.numero, Validators.minLength(5))
        required(polku.nimiFi)
        required(polku.nimiEn)
        disabled(polku.alv, () => this._alvPoissa())
        required(polku.alv)
        required(polku.alvtyyppi)
        disabled(polku.aktiivinen)
        disabled(polku.automaattinenPoistoProsentti, () => this._poistotPoissa())
        required(polku.automaattinenPoistoProsentti)
        validoiReaktiivisesti(polku.automaattinenPoistoProsentti, Validators.min(0))
        validoiReaktiivisesti(polku.automaattinenPoistoProsentti, Validators.max(100))
        disabled(polku.automaattinenPoistoTili, () => this._poistotPoissa())
        required(polku.automaattinenPoistoTili)
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake.alvtyyppi)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(tyyppi => {
        if (tyyppi === KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA) {
          asetaArvo(this.lomake.alv, null)
          this._alvPoissa.set(true)
        } else {
          this._alvPoissa.set(false)
        }
      })

    asetaArvo(this.lomake.alvtyyppi, this._data.tili.alvTyyppi ?? null)

    // Signal Forms asettaa kentän käytöstilan ensimmäisessä muutostunnistuksessa, mikä nollaa Materialin kentän kohdistustilan
    afterNextRender(() => this.nimiFiInput.focus(), { injector: this._injector })

    muutokset(this.lomake.reskontraActive)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(async isActive => {
        if (
          this._data.tili.reskontraActive && // Was active before
          !isActive // But was just marked as NOT active
        ) {
          const dialogData: AreYouSureDialogData = {
            text: 'Jos reskontra laitetaan pois päältä, kaikki reskontramerkinnät poistetaan.',
            rightAction: 'Kyllä',
          }
          const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())

          if (!isSure) {
            asetaArvo(this.lomake.reskontraActive, true)
          }
        }
      })

    muutokset(this.lomake.automaattinenPoisto)
      .pipe(startWith(this.lomake.automaattinenPoisto().value()), takeUntil(this._ngUnsubscribe))
      .subscribe(automaattinenPoisto => {
        if (automaattinenPoisto) {
          this._asetaPoistotPoissa(false)
        } else {
          this._asetaPoistotPoissa(true)
        }
      })

    muutokset(this.lomake.automaattinenPoistoTili)
      .pipe(debounceTime(50), startWith(''), takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        const suodatetut =
          typeof value === 'string'
            ? this._data.tilit.filter(tili => tili.kirjanpitotili.nimi.toLowerCase().indexOf(value) === 0 || tili.kirjanpitotili.numero.toLowerCase().indexOf(value) === 0)
            : this._data.tilit
        this.suodatetutTilitSubject.next(suodatetut)
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private _asetaPoistotPoissa(poissa: boolean) {
    this._poistotPoissa.set(poissa)
    // Käytöstilan vaihto lähettää poistotilin arvon tilaajille
    asetaArvo(this.lomake.automaattinenPoistoTili, this.lomake.automaattinenPoistoTili().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return this.onkoVirhe(kentta, laji) && (tila.dirty() || tila.touched())
  }

  displayPoistotili(tili: Kirjanpitotili): string {
    return tili && tili.nimi && tili.numero ? `${tili.numero} ${tili.nimi}` : ''
  }

  @HostListener('document:keydown.esc')
  peruuta() {
    this._dialogRef.close(false)
  }

  async poista() {
    this._ladataanService.aloitaLataaminen()
    const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    this._tilikarttaService
      .poistaAsiakkaanPaatilikartanYliajoTili(this._data.asiakas, this._data.tili, kirjanpitajanTiedot.uid)
      .then(() => {
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close(true)
      })
      .catch(error => {
        this._ladataanService.lopetaLataaminen()
        this.commonError = 'Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.'
        this._errorHandler.handleError(error)
      })
  }

  @HostListener('document:keydown.enter')
  async tallenna() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    const localisedName: MonikielinenTeksti = {
      fi: null,
      en: null,
    }

    if (this.lomake.nimiFi().value()) {
      localisedName.fi = this.lomake.nimiFi().value()
    }
    if (this.lomake.nimiEn().value()) {
      localisedName.en = this.lomake.nimiEn().value()
    }
    const tili: Kirjanpitotili = {
      aktiivinen: !!this.lomake.aktiivinen().value(),
      numero: this.lomake.numero().value(),
      nimi: this.lomake.nimiFi().value(),
      oletusAlvKasittely: !this.lomake.alv().disabled() ? this.lomake.alv().value() : null,
      vanhempi: this._data.tili.vanhempi,
      alvTyyppi: this.lomake.alvtyyppi().value(),
      localisedName: localisedName,
    }

    if (this.lomake.automaattinenPoisto().value()) {
      tili.poistotiedot = {
        poistoprosentti: this.lomake.automaattinenPoistoProsentti().value(),
        poistotili: this.lomake.automaattinenPoistoTili().value().numero,
      }
    }

    if (!this.lomake.reskontraActive().disabled()) {
      tili.reskontraActive = this.lomake.reskontraActive().value() ?? false
    }

    const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    this._tilikarttaService
      .tallennaAsiakkaanPaatilikartanYliajoTili(this._data.asiakas, tili, kirjanpitajanTiedot.uid)
      .then(() => {
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close(true)
      })
      .catch(error => {
        this._ladataanService.lopetaLataaminen()
        this.commonError = 'Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.'
        this._errorHandler.handleError(error)
      })
  }
}
